import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, unref as _unref, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "bet-builder"
}
const _hoisted_2 = {
  key: 0,
  class: "bet-builder-card bet-builder-card--idle"
}
const _hoisted_3 = { class: "bet-builder-intro" }
const _hoisted_4 = { class: "bet-builder-eyebrow" }
const _hoisted_5 = { class: "bet-builder-heading" }
const _hoisted_6 = { class: "bet-builder-lead" }
const _hoisted_7 = {
  key: 1,
  class: "bet-builder-card bet-builder-card--open"
}
const _hoisted_8 = { class: "bet-builder-bar" }
const _hoisted_9 = { class: "bet-builder-bar-text" }
const _hoisted_10 = { class: "bet-builder-bar-title" }
const _hoisted_11 = {
  key: 0,
  class: "bet-builder-session-count"
}
const _hoisted_12 = ["aria-label"]
const _hoisted_13 = { class: "bet-builder-body" }
const _hoisted_14 = {
  key: 0,
  class: "bet-builder-config"
}
const _hoisted_15 = { class: "bet-builder-body-lead" }
const _hoisted_16 = { class: "bet-builder-fields" }
const _hoisted_17 = { class: "bet-builder-field" }
const _hoisted_18 = { class: "bet-builder-label" }
const _hoisted_19 = { class: "bet-builder-control" }
const _hoisted_20 = { class: "bet-builder-step-buttons" }
const _hoisted_21 = ["aria-label"]
const _hoisted_22 = ["aria-label"]
const _hoisted_23 = { class: "bet-builder-field" }
const _hoisted_24 = { class: "bet-builder-label" }
const _hoisted_25 = { class: "bet-builder-control" }
const _hoisted_26 = ["min", "max", "step"]
const _hoisted_27 = { class: "bet-builder-step-buttons" }
const _hoisted_28 = ["aria-label"]
const _hoisted_29 = ["aria-label"]
const _hoisted_30 = {
  key: 0,
  class: "bet-builder-error"
}
const _hoisted_31 = ["disabled"]
const _hoisted_32 = {
  key: 1,
  class: "bet-builder-scan"
}
const _hoisted_33 = { class: "bet-builder-scan-row" }
const _hoisted_34 = ["aria-valuenow"]
const _hoisted_35 = { class: "bet-builder-scan-note" }
const _hoisted_36 = {
  key: 2,
  class: "bet-builder-result"
}
const _hoisted_37 = { class: "bet-builder-result-top" }
const _hoisted_38 = { class: "bet-builder-result-label" }
const _hoisted_39 = { class: "bet-builder-result-payout" }
const _hoisted_40 = { class: "bet-builder-result-draw" }
const _hoisted_41 = { class: "bet-builder-result-market" }
const _hoisted_42 = {
  key: 0,
  class: "bet-builder-numbers"
}
const _hoisted_43 = { class: "bet-builder-result-meta" }
const _hoisted_44 = { class: "bet-builder-actions" }

import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBetBuilderStore } from '@/stores/betBuilder'
import { useIntegrationMode } from '@/composables/useIntegrationMode'
import { useGameStore } from '@/stores/game'
import { useBetslipStore } from '@/stores/betslip'
import { useToast } from '@/composables/useToast'
import { formatMarketLabel, matchToBetslipItem } from '@/types/betBuilder'
import { iframeCommunication } from '@/utils/iframeCommunication'


export default /*@__PURE__*/_defineComponent({
  __name: 'BetBuilderPanel',
  setup(__props) {

const { t } = useI18n()
const betBuilderStore = useBetBuilderStore()
const gameStore = useGameStore()
const betslipStore = useBetslipStore()
const { showToast } = useToast()
const { isLite } = useIntegrationMode()

const localTargetWin = ref(betBuilderStore.targetWin)
const localStake = ref(betBuilderStore.stake)

const isEnabled = computed(() =>
  !isLite.value && betBuilderStore.isModuleEnabled(gameStore.getBetSettings?.enabled_modules)
)

const phase = computed(() => betBuilderStore.phase)
const match = computed(() => betBuilderStore.match)
const error = computed(() => betBuilderStore.error)
const scanProgress = computed(() => betBuilderStore.scanProgress)
const sessionAddedCount = computed(() => betBuilderStore.sessionAddedCount)

const betMin = computed(() => Number(gameStore.getBetSettings?.bet_min) || 1)
const betMax = computed(() => Number(gameStore.getBetSettings?.bet_max) || 100000)
const stakeStep = computed(() => (gameStore.getBetSettings?.allow_decimals ? 0.5 : 1))
const currencySymbol = computed(() => gameStore.getBetSettings?.currency_symbol || '')

const useOnlinePlatformBetslip = computed(() => {
  const ch = gameStore.getBetSettings?.client_surface_ui?.online
  return ch?.use_platform_betslip !== false
})

const canBuild = computed(() => localTargetWin.value > 0 && localStake.value >= betMin.value)

const displayNumbers = computed(() => {
  if (!match.value?.numbers?.length) return []
  const main = match.value.numbers[0] || []
  const bonus = match.value.numbers[1] || []
  return [...main, ...bonus]
})

watch(
  () => gameStore.getBetSettings?.bet_default,
  (defaultStake) => {
    if (typeof defaultStake === 'number' && defaultStake > 0 && betBuilderStore.phase === 'idle') {
      betBuilderStore.stake = defaultStake
      localStake.value = defaultStake
    }
  },
  { immediate: true }
)

function formatMoney(value: number): string {
  const symbol = currencySymbol.value
  const rounded = Math.round(value)
  return symbol ? `${symbol}${rounded.toLocaleString('en-US')}` : rounded.toLocaleString('en-US')
}

function formatDrawTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function openPanel() {
  betBuilderStore.openConfig(localStake.value, localTargetWin.value)
  localTargetWin.value = betBuilderStore.targetWin
  localStake.value = betBuilderStore.stake
}

function closePanel() {
  betBuilderStore.close()
}

function adjustTarget(delta: number) {
  localTargetWin.value = Math.max(1, Math.round((localTargetWin.value || 0) + delta))
}

function adjustStake(delta: number) {
  const next = Math.max(betMin.value, (localStake.value || 0) + delta)
  localStake.value = betMax.value > 0 ? Math.min(next, betMax.value) : next
}

async function runMatch() {
  betBuilderStore.targetWin = localTargetWin.value
  betBuilderStore.stake = localStake.value
  await betBuilderStore.findMatch()
}

function confirmBet(continueBuilding: boolean) {
  if (!match.value) return

  const betslipItem = matchToBetslipItem(match.value)

  if (!useOnlinePlatformBetslip.value) {
    iframeCommunication.sendMessage('bets-added', { bets: [betslipItem] })
    try {
      window.dispatchEvent(
        new CustomEvent('numbers-online-betslip-selection', { detail: { bets: [betslipItem] } })
      )
    } catch {
      /* ignore */
    }
    showToast({
      title: t('toast.selectionSent'),
      message: t('toast.externalIntegrationMsg'),
      type: 'info',
      duration: 4000,
    })
    if (continueBuilding) {
      betBuilderStore.recordAdded()
      betBuilderStore.returnToConfig()
    } else {
      betBuilderStore.close()
    }
    return
  }

  const added = betslipStore.addBet(betslipItem)
  if (!added) {
    showToast({
      title: t('toast.betAlreadyAdded'),
      message: t('toast.betAlreadyAddedMsg'),
      type: 'info',
      duration: 3000,
    })
    if (continueBuilding) {
      betBuilderStore.returnToConfig()
    }
    return
  }

  iframeCommunication.sendMessage('bets-added', { bets: [betslipItem] })
  betBuilderStore.recordAdded()
  showToast({
    title: t('betBuilder.addedTitle'),
    message: continueBuilding
      ? t('betBuilder.addedContinueMessage', { game: match.value.game_name })
      : t('betBuilder.addedMessage', { game: match.value.game_name }),
    type: 'success',
    duration: 3000,
  })

  if (continueBuilding) {
    betBuilderStore.returnToConfig()
  } else {
    betBuilderStore.close()
  }
}

return (_ctx: any,_cache: any) => {
  return (isEnabled.value)
    ? (_openBlock(), _createElementBlock("section", _hoisted_1, [
        (phase.value === 'idle')
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.$t('betBuilder.badge')), 1),
                _createElementVNode("h2", _hoisted_5, _toDisplayString(_ctx.$t('betBuilder.title')), 1),
                _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.$t('betBuilder.subtitle')), 1)
              ]),
              _createElementVNode("button", {
                type: "button",
                class: "bet-builder-primary bet-builder-primary--compact",
                onClick: openPanel
              }, _toDisplayString(_ctx.$t('betBuilder.start')), 1)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("h2", _hoisted_10, _toDisplayString(_ctx.$t('betBuilder.title')), 1),
                  (sessionAddedCount.value > 0)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(_ctx.$t('betBuilder.addedCount', { count: sessionAddedCount.value })), 1))
                    : _createCommentVNode("", true)
                ]),
                (phase.value !== 'scanning')
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      type: "button",
                      class: "bet-builder-dismiss",
                      "aria-label": _ctx.$t('common.close'),
                      onClick: closePanel
                    }, _cache[9] || (_cache[9] = [
                      _createElementVNode("span", { "aria-hidden": "true" }, "×", -1)
                    ]), 8, _hoisted_12))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_13, [
                (phase.value === 'config')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                      _createElementVNode("p", _hoisted_15, _toDisplayString(_ctx.$t('betBuilder.configLead')), 1),
                      _createElementVNode("div", _hoisted_16, [
                        _createElementVNode("label", _hoisted_17, [
                          _createElementVNode("span", _hoisted_18, _toDisplayString(_ctx.$t('betBuilder.targetWin')), 1),
                          _createElementVNode("div", _hoisted_19, [
                            _withDirectives(_createElementVNode("input", {
                              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((localTargetWin).value = $event)),
                              type: "number",
                              min: "1",
                              step: "1",
                              inputmode: "numeric",
                              class: "bet-builder-input"
                            }, null, 512), [
                              [
                                _vModelText,
                                localTargetWin.value,
                                void 0,
                                { number: true }
                              ]
                            ]),
                            _createElementVNode("div", _hoisted_20, [
                              _createElementVNode("button", {
                                type: "button",
                                class: "bet-builder-step-btn",
                                "aria-label": _ctx.$t('betBuilder.increaseTarget'),
                                onClick: _cache[1] || (_cache[1] = ($event: any) => (adjustTarget(100)))
                              }, " + ", 8, _hoisted_21),
                              _createElementVNode("button", {
                                type: "button",
                                class: "bet-builder-step-btn",
                                "aria-label": _ctx.$t('betBuilder.decreaseTarget'),
                                onClick: _cache[2] || (_cache[2] = ($event: any) => (adjustTarget(-100)))
                              }, " − ", 8, _hoisted_22)
                            ])
                          ])
                        ]),
                        _createElementVNode("label", _hoisted_23, [
                          _createElementVNode("span", _hoisted_24, _toDisplayString(_ctx.$t('betBuilder.stake')), 1),
                          _createElementVNode("div", _hoisted_25, [
                            _withDirectives(_createElementVNode("input", {
                              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((localStake).value = $event)),
                              type: "number",
                              min: betMin.value,
                              max: betMax.value,
                              step: stakeStep.value,
                              inputmode: "decimal",
                              class: "bet-builder-input"
                            }, null, 8, _hoisted_26), [
                              [
                                _vModelText,
                                localStake.value,
                                void 0,
                                { number: true }
                              ]
                            ]),
                            _createElementVNode("div", _hoisted_27, [
                              _createElementVNode("button", {
                                type: "button",
                                class: "bet-builder-step-btn",
                                "aria-label": _ctx.$t('betBuilder.increaseStake'),
                                onClick: _cache[4] || (_cache[4] = ($event: any) => (adjustStake(stakeStep.value)))
                              }, " + ", 8, _hoisted_28),
                              _createElementVNode("button", {
                                type: "button",
                                class: "bet-builder-step-btn",
                                "aria-label": _ctx.$t('betBuilder.decreaseStake'),
                                onClick: _cache[5] || (_cache[5] = ($event: any) => (adjustStake(-stakeStep.value)))
                              }, " − ", 8, _hoisted_29)
                            ])
                          ])
                        ])
                      ]),
                      (error.value)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_30, _toDisplayString(error.value), 1))
                        : _createCommentVNode("", true),
                      _createElementVNode("button", {
                        type: "button",
                        class: "bet-builder-primary bet-builder-primary--full",
                        disabled: !canBuild.value,
                        onClick: runMatch
                      }, _toDisplayString(_ctx.$t('betBuilder.build')), 9, _hoisted_31)
                    ]))
                  : (phase.value === 'scanning')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_32, [
                        _createElementVNode("div", _hoisted_33, [
                          _createElementVNode("span", null, _toDisplayString(_ctx.$t('betBuilder.scanning')), 1),
                          _createElementVNode("span", null, _toDisplayString(scanProgress.value) + "%", 1)
                        ]),
                        _createElementVNode("div", {
                          class: "bet-builder-progress",
                          role: "progressbar",
                          "aria-valuenow": scanProgress.value,
                          "aria-valuemin": "0",
                          "aria-valuemax": "100"
                        }, [
                          _createElementVNode("div", {
                            class: "bet-builder-progress-fill",
                            style: _normalizeStyle({ width: `${scanProgress.value}%` })
                          }, null, 4)
                        ], 8, _hoisted_34),
                        _createElementVNode("p", _hoisted_35, _toDisplayString(_ctx.$t('betBuilder.scanNote')), 1)
                      ]))
                    : (phase.value === 'result' && match.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_36, [
                          _createElementVNode("div", _hoisted_37, [
                            _createElementVNode("span", _hoisted_38, _toDisplayString(_ctx.$t('betBuilder.bestMatch')), 1),
                            _createElementVNode("span", _hoisted_39, _toDisplayString(formatMoney(match.value.payout)), 1)
                          ]),
                          _createElementVNode("div", _hoisted_40, [
                            _createElementVNode("strong", null, _toDisplayString(match.value.game_name), 1),
                            _createElementVNode("span", null, _toDisplayString(formatDrawTime(match.value.draw.draw_date)), 1)
                          ]),
                          _createElementVNode("p", _hoisted_41, _toDisplayString(_unref(formatMarketLabel)(match.value.pick, match.value.odds)), 1),
                          (displayNumbers.value.length)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_42, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayNumbers.value, (num, index) => {
                                  return (_openBlock(), _createElementBlock("span", {
                                    key: `${num}-${index}`,
                                    class: _normalizeClass(["number-ball", { bonus: index >= (match.value.numbers[0]?.length ?? 0) }])
                                  }, _toDisplayString(num), 3))
                                }), 128))
                              ]))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_43, [
                            _createElementVNode("span", null, _toDisplayString(_ctx.$t('betBuilder.stake')) + ": " + _toDisplayString(formatMoney(match.value.stake)), 1),
                            _createElementVNode("span", null, _toDisplayString(_ctx.$t('betBuilder.targetWin')) + ": " + _toDisplayString(formatMoney(match.value.target_win)), 1)
                          ]),
                          _createElementVNode("div", _hoisted_44, [
                            _createElementVNode("button", {
                              type: "button",
                              class: "bet-builder-secondary",
                              onClick: _cache[6] || (_cache[6] = ($event: any) => (_unref(betBuilderStore).returnToConfig()))
                            }, _toDisplayString(_ctx.$t('betBuilder.buildAnother')), 1),
                            _createElementVNode("button", {
                              type: "button",
                              class: "bet-builder-primary",
                              onClick: _cache[7] || (_cache[7] = ($event: any) => (confirmBet(false)))
                            }, _toDisplayString(_ctx.$t('betBuilder.addToBetslip')), 1)
                          ]),
                          _createElementVNode("button", {
                            type: "button",
                            class: "bet-builder-tertiary",
                            onClick: _cache[8] || (_cache[8] = ($event: any) => (confirmBet(true)))
                          }, _toDisplayString(_ctx.$t('betBuilder.addAndContinue')), 1)
                        ]))
                      : _createCommentVNode("", true)
              ])
            ]))
      ]))
    : _createCommentVNode("", true)
}
}

})